// Head-to-head duels and kickoff-gated player pick reveals.
import { httpsCallable } from 'firebase/functions';
import type { Duel, Fixture, Prediction, Round, Sport } from '@h2h/shared';
import { functions } from '../firebase';

/** Challenge a friend to a duel for a round (defaults to the league's current round). */
export async function challengeFriend(
  leagueId: string,
  opponentId: string,
  roundId?: string
): Promise<{ ok: true; duel: Duel }> {
  const call = httpsCallable<{ leagueId: string; opponentId: string; roundId?: string }, { ok: true; duel: Duel }>(
    functions,
    'challengeFriend'
  );
  return (await call({ leagueId, opponentId, roundId })).data;
}

/** Accept or decline a pending duel. */
export async function respondToChallenge(duelId: string, accept: boolean): Promise<{ ok: true; duel: Duel }> {
  const call = httpsCallable<{ duelId: string; accept: boolean }, { ok: true; duel: Duel }>(
    functions,
    'respondToChallenge'
  );
  return (await call({ duelId, accept })).data;
}

/** Withdraw a pending challenge (challenger only). */
export async function cancelChallenge(duelId: string): Promise<{ ok: true; duel: Duel }> {
  const call = httpsCallable<{ duelId: string }, { ok: true; duel: Duel }>(functions, 'cancelChallenge');
  return (await call({ duelId })).data;
}

/** One side of a duel as returned by getDuel — opponent picks are kickoff-gated. */
export interface DuelSidePrediction extends Partial<Prediction> {
  fixtureId: string;
  /** True when the pick exists but its fixture hasn't kicked off (hidden). */
  locked?: boolean;
}
/**
 * One revealed (or stage-start-gated) cycling duel call. A cycling stage is EXACT,
 * so the opponent's whole card reveals together once the stage departs; `points` is
 * the post-confidence award once the duel settles (0 on a miss/void), else null.
 */
export interface DuelSideDuelPick {
  duelId: string;
  /** Backed side — present once revealed. */
  pick?: 'A' | 'B';
  confidenceWeight?: number;
  points?: number | null;
  /** True when the pick exists but the stage hasn't departed (hidden). */
  locked?: boolean;
}
export interface DuelSide {
  userId: string;
  displayName: string;
  avatarTone: number;
  score: number;
  /** Football market predictions (empty for cycling). */
  predictions: DuelSidePrediction[];
  /** Cycling binary duel calls (present only for cycling duels). */
  duelPicks?: DuelSideDuelPick[];
}
export interface DuelDetail {
  ok: true;
  duel: Duel;
  round: Round | null;
  /** Which board to render: football market picks vs cycling rider duels. */
  sport: Sport;
  fixtures: Fixture[];
  challenger: DuelSide;
  opponent: DuelSide;
}

/** Read a duel with the kickoff-gated pick-by-pick comparison. */
export async function getDuel(duelId: string): Promise<DuelDetail> {
  const call = httpsCallable<{ duelId: string }, DuelDetail>(functions, 'getDuel');
  return (await call({ duelId })).data;
}

/** One revealed football pick on a profile — the prediction plus the points it
 *  banked (null until its fixture settles). Only kicked-off picks are returned. */
export interface PlayerPickReveal extends Prediction {
  points: number | null;
}

/** One revealed cycling duel call on a profile. */
export interface PlayerDuelPickReveal {
  duelId: string;
  pick: 'A' | 'B';
  confidenceWeight: number;
  points: number | null;
}

export interface PlayerRoundPicks {
  userId: string;
  displayName: string;
  avatarTone: number;
  /** Live/settled round score (same engine as the board). */
  score: number;
  /** Football market picks (empty for cycling). */
  predictions: PlayerPickReveal[];
  /** Cycling binary duel calls (empty for football). */
  duelPicks: PlayerDuelPickReveal[];
  /** Picks not yet revealed (fixture hasn't kicked off) — counted, never listed. */
  lockedCount: number;
  /** False when the player never entered this round. */
  hasEntry: boolean;
}

/** A round the player entered, for the profile's round-history switcher (newest first). */
export interface EnteredRound {
  roundId: string;
  label: string;
  firstKickoffAt: number;
  status: Round['status'];
}

export interface PlayerRoundDetail {
  ok: true;
  sport: Sport;
  round: Round | null;
  fixtures: Fixture[];
  /** Rounds this player actually entered (newest first) — drives the history switcher. */
  enteredRounds: EnteredRound[];
  player: PlayerRoundPicks;
  isSelf: boolean;
}

/**
 * Read another player's picks for a round (to show on their profile). Server-side
 * reveal — picks are kickoff-gated, so a rival's card can never be copied before it
 * locks. Defaults to the player's most recently entered round when `roundId` is
 * omitted. The caller must be a member of the league.
 */
export async function getPlayerRound(
  leagueId: string,
  userId: string,
  roundId?: string
): Promise<PlayerRoundDetail> {
  const call = httpsCallable<{ leagueId: string; userId: string; roundId?: string }, PlayerRoundDetail>(
    functions,
    'getPlayerRound'
  );
  return (await call({ leagueId, userId, roundId })).data;
}
